// src/components/InvoicesAnalytics.js
import React, { useState, useEffect, useMemo } from "react";
import { Line, Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Tooltip,
  Legend,
} from "chart.js";
import { useNavigate, useLocation } from "react-router-dom";
import { FiArrowLeft } from "react-icons/fi";
import { getCompanyData } from "../../Service/apiService";
import "../../assets/css/Analytics.css";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css"; 

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Tooltip,
  Legend
);

const toLower = (val) => (val || "").toLowerCase().trim();

const parseDate = (str) => {
  if (!str) return null;
  if (typeof str === "string" && /^\d{4}-\d{1,2}$/.test(str)) {
    return new Date(str + "-01");
  }
  const date = new Date(str);
  return isNaN(date) ? null : date;
};

export default function InvoicesAnalytics() {
  const navigate = useNavigate();
  const location = useLocation();

  const [timeRange, setTimeRange] = useState("1y");
  const [selectedCategory, setSelectedCategory] = useState("total");
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);

  // Custom date state
  const [customStartDate, setCustomStartDate] = useState("");
  const [customEndDate, setCustomEndDate] = useState("");

  useEffect(() => {
    fetchCompanies();
  }, []);

  const fetchCompanies = async () => {
    setLoading(true);
    try {
      const res = await getCompanyData("all");
      const allCompanies = Array.isArray(res?.data) ? res.data : [];
      const invoiced = allCompanies.filter(
        (c) => c.invoiceDate && toLower(c.companyType) !== "internal"
      );
      setCompanies(invoiced);
    } catch (err) {
      console.error("Error fetching companies:", err);
    } finally {
      setLoading(false);
    }
  };

  // ✅ Compute effective date range
  const { computedStartDate, computedEndDate } = useMemo(() => {
    const now = new Date();
    if (timeRange === "custom") {
      const start = customStartDate ? new Date(customStartDate) : new Date(now.getFullYear() - 1, now.getMonth(), 1);
      const end = customEndDate ? new Date(customEndDate) : now;
      return { computedStartDate: start, computedEndDate: end };
    } else {
      let start;
      switch (timeRange) {
        case "1w": start = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000); break;
        case "1m": start = new Date(now.getFullYear(), now.getMonth() - 1, 1); break;
        case "3m": start = new Date(now.getFullYear(), now.getMonth() - 3, 1); break;
        case "6m": start = new Date(now.getFullYear(), now.getMonth() - 6, 1); break;
        case "1y":
        default: start = new Date(now.getFullYear() - 1, now.getMonth(), 1);
      }
      return { computedStartDate: start, computedEndDate: now };
    }
  }, [timeRange, customStartDate, customEndDate]);

  const categorized = useMemo(() => {
    return {
      total: companies,
      new: companies.filter(c => toLower(c.companyType) === "new"),
      renewal: companies.filter(c => toLower(c.companyType) === "renewal"),
      india: companies.filter(c => toLower(c.country) === "india"),
      foreign: companies.filter(c => toLower(c.country) !== "india"),
    };
  }, [companies]);

  const barData = useMemo(() => {
    const data = [
      categorized.new.length,
      categorized.renewal.length,
      categorized.india.length,
      categorized.foreign.length,
    ];
    const total = companies.length;
    const renewalPct = total ? Math.round((categorized.renewal.length / total) * 100) : 0;
    return {
      labels: ["New", "Renewal", "India", "Foreign"],
      data,
      total,
      renewalPct,
    };
  }, [categorized, companies.length]);

  // ✅ FIXED: Use computedStartDate, and generate monthly labels properly
  const timeChartData = useMemo(() => {
    const source = selectedCategory === "total"
      ? companies
      : categorized[selectedCategory] || [];

    if (source.length === 0) {
      return { labels: [], datasets: [] };
    }

    // Generate all month buckets between computedStartDate and computedEndDate
    const months = [];
    let current = new Date(computedStartDate.getFullYear(), computedStartDate.getMonth(), 1);
    const end = new Date(computedEndDate.getFullYear(), computedEndDate.getMonth() + 1, 0);

    while (current <= end) {
      months.push(new Date(current));
      current.setMonth(current.getMonth() + 1);
    }

    const monthLabels = months.map(m =>
      m.toLocaleString('default', { month: 'short', year: 'numeric' })
    );

    const monthlyMap = {};
    monthLabels.forEach(label => monthlyMap[label] = 0);

    source.forEach(c => {
      const date = parseDate(c.invoiceDate);
      if (!date || date < computedStartDate || date > computedEndDate) return;

      for (let i = 0; i < months.length; i++) {
        const monthStart = new Date(months[i].getFullYear(), months[i].getMonth(), 1);
        const monthEnd = new Date(months[i].getFullYear(), months[i].getMonth() + 1, 0);
        if (date >= monthStart && date <= monthEnd) {
          monthlyMap[monthLabels[i]] += 1;
          break;
        }
      }
    });

    const labels = monthLabels;
    const data = labels.map(label => monthlyMap[label]);

    const categoryLabel = selectedCategory === "total"
      ? "Total Invoices"
      : (selectedCategory === "india" || selectedCategory === "foreign"
        ? `${selectedCategory.charAt(0).toUpperCase() + selectedCategory.slice(1)} Invoices`
        : `${selectedCategory.charAt(0).toUpperCase() + selectedCategory.slice(1)} Invoices`);

    return {
      labels,
      datasets: [{
        label: categoryLabel,
        data,
        borderColor: "rgba(5, 55, 99, 1)",
        backgroundColor: "rgba(5, 55, 99, 0.1)",
        tension: 0.3,
        pointRadius: 3,
        pointBackgroundColor: "rgba(5, 55, 99, 1)",
        pointBorderColor: "#fff",
        pointBorderWidth: 1,
      }],
    };
  }, [companies, categorized, selectedCategory, computedStartDate, computedEndDate]);

  const handleBarClick = (elements) => {
    if (elements.length > 0) {
      const index = elements[0].index;
      const keys = ["new", "renewal", "india", "foreign"];
      setSelectedCategory(keys[index]);
    }
  };

  const handleBack = () => {
    const dashboardPath = location.pathname.replace("/analytics", "/dashboard");
    navigate(dashboardPath);
  };

  const timeOptions = [
    { label: "1 Week", value: "1w" },
    { label: "1 Month", value: "1m" },
    { label: "3 Months", value: "3m" },
    { label: "6 Months", value: "6m" },
    { label: "1 Year", value: "1y" },
    { label: "Custom", value: "custom" },
  ];

  const barOptions = {
    responsive: true,
    maintainAspectRatio: false,
    indexAxis: "y",
    plugins: {
      legend: { display: false },
      tooltip: {
        backgroundColor: "#fff",
        titleColor: "rgb(5, 55, 99)",
        bodyColor: "#333",
        borderColor: "#eee",
        borderWidth: 1,
        padding: 10,
        borderRadius: 6,
      },
    },
    scales: {
      x: {
        beginAtZero: true,
        grid: { color: "rgba(0,0,0,0.04)" },
        ticks: { color: "#666", font: { size: 11 } },
        title: { display: true, text: "Number of Invoices", color: "#666", font: { size: 12, weight: "bold" } },
      },
      y: {
        grid: { display: false },
        ticks: { color: "#333", font: { size: 12, weight: "500" } },
      },
    },
    onClick: (_evt, elements) => handleBarClick(elements),
    onHover: (evt, elements) => {
      evt.native.target.style.cursor = elements.length > 0 ? "pointer" : "default";
    },
  };

  const lineOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        backgroundColor: "#fff",
        titleColor: "rgb(5, 55, 99)",
        bodyColor: "#333",
        borderColor: "#eee",
        borderWidth: 1,
        padding: 10,
        borderRadius: 6,
      },
    },
    scales: {
      x: {
        title: { display: true, text: "Month", color: "#666", font: { size: 12, weight: "bold" } },
        grid: { color: "rgba(0,0,0,0.04)" },
        ticks: { color: "#666", font: { size: 11 } },
      },
      y: {
        title: { display: true, text: "Number of Invoices", color: "#666", font: { size: 12, weight: "bold" } },
        beginAtZero: true,
        grid: { color: "rgba(0,0,0,0.04)" },
        ticks: { color: "#666", font: { size: 11 }, precision: 0 },
      },
    },
  };

  return (
    <div className="analytics-container">
      <div className="header-section">
       
        <h1 className="title">Invoices Analytics</h1>
        <div className="time-filter" style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <select
            value={timeRange}
            onChange={(e) => setTimeRange(e.target.value)}
            style={{
              padding: '8px 14px',
              borderRadius: '8px',
              fontSize: '14px',
              background: 'rgba(255, 255, 255, 0.18)',
              border: '1px solid #ddd',
              color: 'rgb(0,0,0)',
              backdropFilter: 'blur(8px)',
              cursor: 'pointer',
            }}
          >
            {timeOptions.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
          {timeRange === "custom" && (
            <div style={{ display: 'flex', gap: '8px' }}>
              <input
                type="date"
                value={customStartDate}
                onChange={(e) => setCustomStartDate(e.target.value)}
                style={{
                  padding: '6px',
                  fontSize: '12px',
                  borderRadius: '4px',
                  border: '1px solid #ccc',
                }}
              />
              <input
                type="date"
                value={customEndDate}
                onChange={(e) => setCustomEndDate(e.target.value)}
                style={{
                  padding: '6px',
                  fontSize: '12px',
                  borderRadius: '4px',
                  border: '1px solid #ccc',
                }}
              />
            </div>
          )}
        </div>
      </div>

      {loading ? (
        <div className="loading">Loading invoices...</div>
      ) : (
        <div className="analytics-content">
          {/* Left: Distribution */}
          <div className="chart-panel">
            <h3>Invoice Distribution</h3>
            <p className="chart-subtitle">
              {barData.total} total invoices
            </p>
            <div className="chart-wrapper">
              <Bar
                data={{
                  labels: barData.labels,
                  datasets: [{
                    label: "Invoices",
                    data: barData.data,
                    backgroundColor: [
                      "rgba(42, 86, 130, 0.85)",    // New
                      "rgba(5, 55, 99, 0.95)",      // Renewal
                      "rgba(66, 111, 155, 0.85)",   // India
                      "rgba(93, 114, 135, 0.85)",   // Foreign
                    ],
                    borderColor: "#fff",
                    borderWidth: 1,
                  }]
                }}
                options={barOptions}
              />
            </div>
          </div>

          {/* Right: Trend */}
          <div className="chart-panel">
            <h3>
              {selectedCategory === "total"
                ? "Total Invoices"
                : `${selectedCategory.charAt(0).toUpperCase() + selectedCategory.slice(1)} Invoices`}
            </h3>
            <div className="chart-wrapper">
              {timeChartData.labels.length > 0 ? (
                <Line data={timeChartData} options={lineOptions} />
              ) : (
                <div className="no-data">No invoices in selected period</div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}